<template>
  <div>
    <div
      v-if="collapsed"
      class="accountButton"
      test-id="account_button"
      @click="openAccountDialog"
    >
      <el-icon class="accountDetailsIcon"><User /></el-icon>
    </div>
    <div
      v-else
      class="accountDetails"
      test-id="account_details"
      @click="openAccountDialog"
    >
      <div class="userWrapper">
        <el-icon class="accountDetailsIcon"><User /></el-icon>
      </div>
      <div class="accountDetailsText">
        <span class="accountDetailsName" test-id="account_details_name">{{
          username
        }}</span>
        <span class="accountDetailsRole" test-id="account_details_role">{{
          primaryRole
        }}</span>
      </div>
    </div>

    <el-dialog
      v-model="accountDialog"
      :title="strings.account_details_page_title"
      width="600px"
      @closed="onAccountDialogClosed"
    >
      <el-alert class="infoPanel" type="info" show-icon :closable="false">
        <p>{{ strings.account_details_info }}</p>
      </el-alert>

      <h4 class="accountInfoLabel">{{ strings.account_name_title }}</h4>
      <p class="accountInfo" test-id="account_name">{{ username }}</p>

      <h4 class="accountInfoLabel">{{ strings.account_roles_title }}</h4>
      <div class="accountRoles" test-id="account_roles">
        <el-tag
          v-for="role in roles"
          :key="role"
          round
          effect="plain"
          :type="formatting.roleColour(role)"
          >{{ roleLabel(role) }}</el-tag
        >
      </div>

      <div v-if="isUserAccount">
        <div class="accountPassword accountInfoLabel">
          <h4>{{ strings.account_password_title }}</h4>
          <el-button
            v-if="!editingPassword"
            link
            type="primary"
            class="accountPasswordEdit"
            :icon="icons.edit"
            test-id="edit_password_button"
            @click="startEditingPassword"
          />
        </div>
        <p
          v-if="!editingPassword"
          class="accountInfo"
          test-id="account_password"
        >
          ●●●●●●●●●●●●●●●
        </p>
      </div>

      <el-form
        v-if="editingPassword"
        ref="changePasswordForm"
        class="accountPasswordForm"
        :model="formInput"
        label-position="top"
        @validate="onValidate"
        @submit.prevent="submitForm"
      >
        <el-form-item
          :label="strings.new_password_label"
          prop="password"
          :rules="[
            { required: true, message: '' },
            { validator: validation.validatePassword },
          ]"
          :error="passwordError"
        >
          <el-input
            v-model="formInput.password"
            type="password"
            name="new-password"
            test-id="password_input"
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item
          :label="strings.confirm_password_label"
          prop="confirmPassword"
          :rules="[
            { required: true, message: '' },
            { validator: passwordsMatch },
          ]"
        >
          <el-input
            v-model="formInput.confirmPassword"
            type="password"
            name="confirm-new-password"
            autocomplete="off"
            test-id="password_conf_input"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            native-type="submit"
            :loading="loading"
            :disabled="!formValid"
            test-id="submit_button"
            >{{ strings.update_button }}</el-button
          >
          <el-button
            :disabled="loading"
            test-id="cancel_password_button"
            @click="cancelEditingPassword"
            >{{ strings.cancel_button }}</el-button
          >
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button
            test-id="close_account_button"
            @click="accountDialog = false"
            >{{ strings.close_button }}</el-button
          >
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { shallowRef } from "vue";
import { User, Edit } from "@element-plus/icons-vue";
import { ElNotification } from "element-plus";
import users from "@/api/users.js";
import strings from "@/resources/strings.js";
import store from "@/store/index.js";
import validation from "@/utils/validation.js";
import formatting from "@/utils/formatting.js";

export default {
  name: "AccountDetails",
  components: { User },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      roleInfo: {},
      icons: { edit: shallowRef(Edit) },
      accountDialog: false,
      editingPassword: false,
      formInput: {
        name: "",
        password: "",
        confirmPassword: "",
      },
      formValidity: {
        password: false,
        confirmPassword: false,
      },
      loading: false,
      passwordError: "",
      validation: validation,
      formatting: formatting,
      strings: strings,
    };
  },
  computed: {
    username() {
      return store.getters["auth/getUsername"];
    },
    roles() {
      return store.getters["auth/getRoles"];
    },
    primaryRole() {
      const role = this.roles[0];
      return role ? this.roleLabel(role) : "";
    },
    isUserAccount() {
      return this.roleInfo[this.roles[0]]?.type === "user_controlled";
    },
    formValid() {
      return this.formValidity.password && this.formValidity.confirmPassword;
    },
  },
  created() {
    this.loadRoleLabels();
  },
  methods: {
    loadRoleLabels() {
      users.getRoles().then((response) => {
        this.roleInfo = response.data;
      });
    },
    roleLabel(role) {
      return this.roleInfo[role]?.label || role;
    },
    openAccountDialog() {
      this.accountDialog = true;
    },
    onAccountDialogClosed() {
      this.editingPassword = false;
      this.resetPasswordForm();
    },
    startEditingPassword() {
      this.resetPasswordForm();
      this.editingPassword = true;
    },
    cancelEditingPassword() {
      this.editingPassword = false;
      this.resetPasswordForm();
    },
    resetPasswordForm() {
      this.formInput.name = this.username || "";
      this.formInput.password = "";
      this.formInput.confirmPassword = "";
      this.formValidity.password = false;
      this.formValidity.confirmPassword = false;
      this.passwordError = "";
      this.loading = false;
      this.$refs.changePasswordForm?.clearValidate();
    },
    onValidate(prop, valid) {
      this.formValidity[prop] = valid;
    },
    passwordsMatch(rule, value, callback) {
      if (value != this.formInput.password) {
        callback(new Error(strings.passwords_not_matching));
      } else {
        callback();
      }
    },
    submitForm: async function () {
      if (!this.formValid) return;

      this.loading = true;
      this.passwordError = "";
      try {
        await users.updatePassword(this.formInput);

        ElNotification({
          title: strings.password_updated_title,
          message: strings.password_updated,
          type: "success",
        });

        this.accountDialog = false;
      } catch (error) {
        this.handleErrorResponse(
          error.response?.status,
          error.response?.data?.detail,
        );
      }
    },
    handleErrorResponse(status, message = "") {
      this.loading = false;
      if (status == 409) {
        this.passwordError = strings.pass_conflict;

        ElNotification({
          title: strings.notification_error_title,
          message: strings.pass_conflict_notification,
          type: "error",
        });
      } else if (status == 400) {
        this.passwordError = message || strings.invalid_password;

        ElNotification({
          title: strings.notification_error_title,
          message: message || strings.invalid_password,
          type: "error",
        });
      } else if (status == 503) {
        ElNotification({
          title: strings.unexpected_server_response,
          message: strings.database_not_available,
          type: "info",
          duration: 0,
        });
      } else {
        ElNotification({
          title: strings.notification_error_title,
          message: strings.failed_to_contact_server,
          type: "error",
        });
      }
    },
  },
};
</script>
